import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { create } from "zustand";
import { useRampState } from "../../../../containers/RampStateContainer";
import { useSnackbar } from "../../../../containers/SnackbarContainer";
import { useUserPreferences } from "../../../../containers/UserPreferencesContainer";
import { Ramp } from "../../../../types";
import { RampCanvas, Shape } from "./RampCanvas/RampCanvas";
import { RampEditorForm } from "./RampEditorForm";
import { useAutoEgress } from "./hooks/useAutoEgress";

interface RampEditorState {
  showReferenceImage: boolean;
  setShowReferenceImage: (show: boolean) => void;
  showMeasurements: boolean;
  setShowMeasurements: (show: boolean) => void;
  showGuideLines: boolean;
  setShowGuideLines: (show: boolean) => void;
  showCalibrationPoints: boolean;
  setShowCalibrationPoints: (show: boolean) => void;
  highlightedCalibrationPoint: string | null;
  setHighlightedCalibrationPoint: (pointId: string | null) => void;
  useV2: boolean;
  setUseV2: (useV2: boolean) => void;
}

export const useRampEditorState = create<RampEditorState>(
  (set): RampEditorState => ({
    showReferenceImage: true,
    setShowReferenceImage: (showReferenceImage: boolean) =>
      set({ showReferenceImage }),
    showMeasurements: true,
    setShowMeasurements: (showMeasurements: boolean) =>
      set({ showMeasurements }),
    showGuideLines: true,
    setShowGuideLines: (showGuideLines: boolean) => set({ showGuideLines }),
    showCalibrationPoints: true,
    setShowCalibrationPoints: (showCalibrationPoints: boolean) =>
      set({ showCalibrationPoints }),
    highlightedCalibrationPoint: null,
    setHighlightedCalibrationPoint: (
      highlightedCalibrationPoint: string | null
    ) => set({ highlightedCalibrationPoint }),
    useV2: false,
    setUseV2: (useV2: boolean) => set({ useV2 }),
  })
);

type Props = {
  onClose: () => void;
};

interface LocalRampState {
  ramp: Ramp | undefined;
  setRamp: (rampOrUpdater: Ramp | ((prevRamp: Ramp) => Ramp)) => void;
  //
  activeShapes: string[];
  setActiveShapes: (activeShapes: string[]) => void;
  getActiveShapes: () => Shape[];
  //
  highlightedShapes: string[];
  setHighlightedShapes: (highlightedShapes: string[]) => void;
  //
  reset: () => void;
}

export const useLocalRampState = create<LocalRampState>(
  (set, get): LocalRampState => ({
    ramp: undefined,
    setRamp: (rampOrUpdater: Ramp | ((prevRamp: Ramp) => Ramp)) => {
      if (typeof rampOrUpdater === 'function') {
        set((state) => {
          if (!state.ramp) {
            console.error('[useLocalRampState] Cannot use functional update when ramp is undefined');
            return state;
          }
          return { ramp: rampOrUpdater(state.ramp) };
        });
      } else {
        set({ ramp: rampOrUpdater });
      }
    },
    //
    activeShapes: [],
    setActiveShapes: (activeShapes: string[]) => set({ activeShapes }),
    getActiveShapes() {
      return get().ramp.shapes.filter((s) => get().activeShapes.includes(s.id));
    },
    //
    highlightedShapes: [],
    setHighlightedShapes: (highlightedShapes: string[]) =>
      set({ highlightedShapes }),
    //
    reset: () => set({
      ramp: undefined,
      activeShapes: [],
      highlightedShapes: []
    }),
  })
);

export const useSelectedToolState = create<{
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
}>((set) => ({
  selectedTool: "Select",
  setSelectedTool: (selectedTool: string) => set({ selectedTool }),
}));

export const useCalibrationState = create<{
  isAddingCalibrationPoint: boolean;
  setIsAddingCalibrationPoint: (adding: boolean) => void;
}>((set) => ({
  isAddingCalibrationPoint: false,
  setIsAddingCalibrationPoint: (isAddingCalibrationPoint: boolean) =>
    set({ isAddingCalibrationPoint }),
}));

export const RampEditorPresenter: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const { ramp, setRamp } = useRampState();
  const { setRamp: setLocalRamp, ramp: localRamp } = useLocalRampState();
  const { selectedTool, setSelectedTool } = useSelectedToolState();
  const snackbar = useSnackbar();
  const { preferences, setPreferences } = useUserPreferences();
  const { showGuideLines } = useRampEditorState();
  const hasLoadedInitialPreferences = React.useRef(false);

  React.useEffect(() => {
    // Only set local ramp if we have a valid ramp from the container
    if (ramp && ramp.id) {
      setLocalRamp(ramp);
    }
  }, [ramp]);

  // Load initial preference once
  React.useEffect(() => {
    if (
      !hasLoadedInitialPreferences.current &&
      preferences.showGuideLines !== undefined
    ) {
      useRampEditorState
        .getState()
        .setShowGuideLines(preferences.showGuideLines);
      hasLoadedInitialPreferences.current = true;
    }
  }, [preferences.showGuideLines]);

  // Save preference when it changes (but not on initial load)
  React.useEffect(() => {
    if (hasLoadedInitialPreferences.current) {
      setPreferences((currentPreferences) => ({
        ...currentPreferences,
        showGuideLines,
      }));
    }
  }, [showGuideLines, setPreferences]);

  // Auto-calculate egress whenever shapes change (debounced)
  useAutoEgress();

  const handleClose = () => {
    onClose();
  };

  const onClickSaveRamp = () => {
    setRamp(localRamp);
    // setRamps(ramps.map((r) => (r.id === localRamp.id ? localRamp : r)));
    snackbar.notify({ text: "Ramp changes saved.", severity: "success" });
  };
  /**
   * make a ramp
   * give it a name
   * upload an image
   * make a reference line to tell us how many pixels = how many feet
   * add an outline
   * add other markings
   */

  if (!localRamp) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={8}
      alignItems="flex-start"
      width="100%"
      height="100%"
    >
      <Box
        sx={{
          p: 1,
          width: 280,
          // position: "sticky", // Makes the Box sticky
          // top: 0, // Stick to the top of the parent container
          alignSelf: "flex-start", // Align properly within the parent
          maxHeight: "100%",
          overflowY: "auto",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <IconButton onClick={() => navigate(`/settings?tab=Ramps`)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle2">Edit Ramp</Typography>
          </Stack>
        </Stack>
        <RampEditorForm
          ramp={localRamp}
          setRamp={setLocalRamp}
          onClickSaveRamp={onClickSaveRamp}
          onClose={onClose}
          selectedTool={selectedTool}
          setSelectedTool={setSelectedTool}
        />
      </Box>
      <Stack
        direction="column"
        spacing={1}
        width="100%"
        sx={{
          overflowY: "auto", // Allow vertical scrolling for this section
          maxHeight: "100vh", // Limit height to viewport for scrollable content
        }}
      >
        <RampCanvas
          ramp={localRamp}
          setRamp={setLocalRamp}
          selectedTool={selectedTool}
          setSelectedTool={setSelectedTool}
        />
      </Stack>
    </Stack>
  );
};
